import React, { useState } from 'react';
import SPAButton from '../../shared/SPAButton';
import SPADatePicker from '../../shared/SPADatePicker';
import SPASelect from '../../shared/SPASelect';
import { PRESETS, activePreset } from './dateRanges';
import filterSelectStyles from './filterSelectStyles';
import { FEATURE_LABELS } from './reportParams';
import SubscriberSelect, { subscriberOption } from './SubscriberSelect';
import type { SubscriberOption } from './SubscriberSelect';
import type { ReportParams, ReportSubscriber } from './types';

interface ReportFiltersProps {
  onApply: (params: ReportParams) => void;
  params: ReportParams;
  /** The scoped district as the report named it, so a deep link shows its name once loaded. */
  subscriber: ReportSubscriber | null;
}

const labelClass = 'tw-mb-1 tw-block tw-text-xs tw-text-spa-secondary';

const FEATURE_OPTIONS = Object.entries(FEATURE_LABELS).map(([value, label]) => ({ label, value }));

const initialSubscriber = (params: ReportParams, subscriber: ReportSubscriber | null): SubscriberOption | null => {
  if (!params.subscriberId) return null;
  if (subscriber && String(subscriber.id) === params.subscriberId) return subscriberOption(subscriber);

  return { label: `#${params.subscriberId}`, value: params.subscriberId };
};

// Edits stay local until Apply; the parent remounts this (via key) when the URL changes.
const ReportFilters = ({ onApply, params, subscriber }: ReportFiltersProps) => {
  const [draft, setDraft] = useState(params);
  const [district, setDistrict] = useState(() => initialSubscriber(params, subscriber));
  const preset = activePreset(draft.from, draft.to);

  return (
    <form
      className="tw-mb-5 tw-flex tw-flex-wrap tw-items-end tw-gap-x-4 tw-gap-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        const { subscriberId, ...rest } = draft;
        onApply(district ? { ...rest, subscriberId: district.value } : rest);
      }}
    >
      <div className="tw-w-48">
        <label className={labelClass} htmlFor="ai-safety-feature">Feature</label>
        <SPASelect
          inputId="ai-safety-feature"
          isSearchable={false}
          options={FEATURE_OPTIONS}
          styles={filterSelectStyles}
          value={FEATURE_OPTIONS.find(option => option.value === draft.feature) ?? FEATURE_OPTIONS[0]}
          onChange={option => option && setDraft({ ...draft, feature: option.value })}
        />
      </div>

      <div className="tw-w-80">
        <label className={labelClass} htmlFor="ai-safety-subscriber">District</label>
        <SubscriberSelect inputId="ai-safety-subscriber" value={district} onChange={setDistrict} />
      </div>

      <fieldset className="tw-m-0 tw-min-w-0 tw-border-0 tw-p-0">
        <legend className={labelClass}>Range</legend>
        <div className="tw-flex tw-gap-1.5">
          {PRESETS.map(item => (
            <SPAButton
              key={item.label}
              aria-pressed={preset === item.label}
              variant={preset === item.label ? 'outline' : 'secondary'}
              onClick={() => setDraft({ ...draft, ...item.range() })}
            >
              {item.label}
            </SPAButton>
          ))}
        </div>
      </fieldset>

      <div className="tw-w-36">
        <label className={labelClass} htmlFor="ai-safety-from">From</label>
        <SPADatePicker
          id="ai-safety-from"
          maxDate={draft.to}
          value={draft.from}
          onChange={from => setDraft({ ...draft, from })}
        />
      </div>

      <div className="tw-w-36">
        <label className={labelClass} htmlFor="ai-safety-to">To</label>
        <SPADatePicker
          id="ai-safety-to"
          minDate={draft.from}
          value={draft.to}
          onChange={to => setDraft({ ...draft, to })}
        />
      </div>

      <SPAButton type="submit" variant="primary">Apply</SPAButton>
    </form>
  );
};

export default ReportFilters;
